import { useGetList } from "ra-core";

import type { SharingRedemptionRow } from "../sharing/sharingContract";
import type { EchoflicksVideoSession } from "../types";
import type { EchoReferralAttribution } from "./attribution";

/**
 * Resolve the referral ledger row behind an Echo's WhatsApp user.
 *
 * Referral relationships remain authoritative in Supabase while Echoes come
 * from the AWS dashboard provider. The composite CRM data provider routes this
 * resource to Supabase even when live Echo data is enabled.
 */
export function useEchoReferral(record?: EchoflicksVideoSession | null): {
  referral: EchoReferralAttribution | null;
  isLoading: boolean;
  isError: boolean;
} {
  const waId = normalizedWhatsAppId(
    record?.whatsapp_user_id ?? record?.contact_id,
  );
  const enabled = Boolean(waId);
  const { data, isPending, error } = useGetList<SharingRedemptionRow>(
    "sharing_redemptions",
    {
      pagination: { page: 1, perPage: 1 },
      sort: { field: "redeemed_at", order: "ASC" },
      filter: { "referred_wa_id@eq": waId },
    },
    { enabled, retry: 1, staleTime: 60_000 },
  );

  const row = data?.[0];
  const referral: EchoReferralAttribution | null = row?.referred_is_new
    ? {
        code: row.code,
        referrerName: row.referrer_name,
        referrerContactId: row.referrer_contact_id,
        redeemedAt: row.redeemed_at,
      }
    : null;
  return {
    referral,
    isLoading: enabled ? isPending : false,
    isError: Boolean(error),
  };
}

function normalizedWhatsAppId(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const digits = value.replace(/[^0-9]/g, "");
  return digits.length >= 6 ? digits : null;
}
